import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Radar } from 'lucide-react'
import { listDevices } from '../../api/devices'
import { PageHeader } from '../../components/layout/PageHeader'
import { EmptyState, StatusBadge } from '../../components/common/Status'
import { HealthBar, SignalBars } from '../../components/common/HealthBar'
import { TimeAgo } from '../../components/common/TimeAgo'
import { formatCoord, formatNum } from '../../lib/format'
import { useUiStore } from '../../stores/ui'
import { cn } from '../../lib/utils'

const HEARTBEAT_WARN_MS = 10 * 60_000

/** 监测站点：在线设备健康表 + 地图定位联动 */
export function StationsPage() {
  const query = useQuery({
    queryKey: ['devices', 'online'],
    queryFn: listDevices,
    refetchInterval: 30_000,
    retry: 1,
  })
  const selectedDeviceId = useUiStore((s) => s.selectedDeviceId)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const devices = query.data ?? []

  return (
    <>
      <PageHeader
        breadcrumb="资源与治理"
        title="监测站点"
        description={`在线设备 ${devices.length} 台 · 心跳超过 10 分钟标黄 · 30s 自动刷新`}
      />
      <div className="panel-glass overflow-hidden rounded-xl">
        {query.isPending ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-9 animate-pulse rounded-md bg-foreground/[.06]" />
            ))}
          </div>
        ) : query.isError ? (
          <EmptyState title="设备列表加载失败" description={query.error.message} icon={<Radar size={28} />} />
        ) : devices.length === 0 ? (
          <EmptyState
            title="暂无在线设备"
            description="设备心跳上报后将在此展示（POST /api/v1/devices/heartbeat）"
            icon={<Radar size={28} />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">设备</th>
                  <th className="px-4 py-2.5 font-medium">状态</th>
                  <th className="px-4 py-2.5 font-medium">电量</th>
                  <th className="px-4 py-2.5 font-medium">信号</th>
                  <th className="px-4 py-2.5 font-medium">最近心跳</th>
                  <th className="px-4 py-2.5 font-medium">位置</th>
                  <th className="px-4 py-2.5 font-medium">首次上线</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {devices.map((d) => {
                  const stale = now - d.lastHeartbeat > HEARTBEAT_WARN_MS
                  return (
                    <tr
                      key={d.deviceId}
                      onClick={() => {
                        useUiStore.getState().selectDevice(d.deviceId)
                        if (d.lat != null && d.lon != null) useUiStore.getState().focusMap(d.lat, d.lon, 10)
                      }}
                      className={cn(
                        'cursor-pointer text-xs transition',
                        stale && 'border-l-2 border-l-warning',
                        selectedDeviceId === d.deviceId ? 'bg-primary/[.07]' : 'hover:bg-foreground/[.03]',
                      )}
                    >
                      <td className="px-4 py-2.5 font-mono text-foreground/90">{d.deviceId}</td>
                      <td className="px-4 py-2.5"><StatusBadge status={d.status} /></td>
                      <td className="px-4 py-2.5"><HealthBar value={d.battery} warnBelow={20} /></td>
                      <td className="px-4 py-2.5"><SignalBars dbm={d.signalStrength} /></td>
                      <td className="px-4 py-2.5"><TimeAgo ts={d.lastHeartbeat} warnAfterMs={HEARTBEAT_WARN_MS} className="text-muted-foreground" /></td>
                      <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">{formatCoord(d.lat, d.lon)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground"><TimeAgo ts={d.firstSeen} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t border-border px-4 py-2 text-[10px] text-muted-foreground/60">
          共 {formatNum(devices.length)} 台 · 点击行可在态势总览地图定位
        </div>
      </div>
    </>
  )
}
